import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { getUserRegistry, setUserRegistry } from "../../services/restAPI/RestApi";
import { Message } from "../../components/BP2Components";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import projectConfig from "../../projectConfig.json";

export const USER_REGISTRY_KEY = "is_grid_nav_editable";
export type NavGridEditability = "1" | "0";

interface UserRegistryContextType {
   isNavGridEnabled: NavGridEditability | null;
   changeNavGridEditability: () => Promise<void>;
}

const UserRegistryContext = createContext<UserRegistryContextType | undefined>(undefined);

const toRegistryString = (value, fallback: "1" | "0" = "1") => {
   if (typeof value === "boolean") return value ? "1" : "0";
   if (value === "1" || value === "0") return value;
   return fallback;
};

export const UserRegistryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const [isNavGridEnabled, setIsNavGridEnabled] = useState<NavGridEditability | null>(null);
   const hasInitialized = useRef(false);
   const intl = useIntl();
   const { authUser } = useSelector(({ auth }) => auth);

   useEffect(() => {
      if (hasInitialized.current) return;

      const handleGetUserRegistryKey = async () => {
         if (typeof authUser !== "number" && authUser !== null) {
            try {
               const defaultGridNavEnabled = projectConfig?.defaultGridNavEnabled;
               const defaultValue = toRegistryString(defaultGridNavEnabled, "1");

               const response = await getUserRegistry(USER_REGISTRY_KEY, defaultValue);

               const value = response?.data?.default?.value ?? "1";

               if (projectConfig?.gridNavSettingsEnabled) {
                  setIsNavGridEnabled(value === "0" ? "0" : "1");
               }
            } catch (error) {
               console.error("Failed to fetch user registry:", error);
               setIsNavGridEnabled("1");
            }
         }
      };

      hasInitialized.current = true;
      handleGetUserRegistryKey();
   }, []);
   const changeNavGridEditability = async () => {
      const valueOfNavGrid = isNavGridEnabled === "1" ? "0" : "1";

      document.activeElement instanceof HTMLElement && document.activeElement?.blur();
      try {
         await setUserRegistry(USER_REGISTRY_KEY, valueOfNavGrid);
         setIsNavGridEnabled(valueOfNavGrid);
         Message.success(intl.formatMessage({ id: "registry.updateSuccess" }));
      } catch (error) {
         Message.error(intl.formatMessage({ id: "registry.failToUpdate" }));
         console.error("Failed to update user registry:", error);
      }
   };

   return (
      <UserRegistryContext.Provider value={{ isNavGridEnabled, changeNavGridEditability }}>
         {children}
      </UserRegistryContext.Provider>
   );
};

export const useUserRegistry = () => {
   const context = useContext(UserRegistryContext);
   if (!context) {
      return {
         isNavGridEnabled: null as NavGridEditability | null,
         changeNavGridEditability: async () => {},
      };
   }
   return context;
};
